import React, { useState, useMemo } from 'react';
import { generatePrescriptionPDF, generatePrintHTML } from '../utils/pdfGenerator';
import prescriptionAPI from '../../../api/prescription';
import { appointmentAPI } from '../../../api/appointment';
import { useToast } from '../../../hooks/useToast';

const formatTiming = (timing = {}) => {
  const slots = [];
  if (timing.morning) slots.push('Morning');
  if (timing.noon) slots.push('Noon');
  if (timing.evening) slots.push('Evening');
  if (timing.night) slots.push('Night');
  return slots.join(', ');
};

const PreviousPrescriptions = ({ 
  selectedPatient, 
  previousPrescriptions, 
  loadingPrescriptions,
  tests = []
}) => {
  const { showError } = useToast();
  const [expandedPrescription, setExpandedPrescription] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [duplicateFlags, setDuplicateFlags] = useState({});

  // Helper: resolve a test (object or id) to display object {name, code}
  const resolveTestObject = (test) => {
    if (!test) return { name: 'Unknown Test', code: '' };
    if (typeof test === 'object') return { name: test.name || 'Unknown Test', code: test.code || '' };
    const found = (tests || []).find(t => (t._id || t.id) === test);
    return { name: found?.name || String(test), code: found?.code || '' };
  };

  // Sort prescriptions so the latest (by createdAt) appears first
  const sortedPrescriptions = useMemo(() => {
    const getTime = (p) => new Date(p?.createdAt || 0).getTime();
    return [...(previousPrescriptions || [])].sort((a, b) => getTime(b) - getTime(a));
  }, [previousPrescriptions]);

  const togglePrescription = (prescriptionId) => {
    setExpandedPrescription(expandedPrescription === prescriptionId ? null : prescriptionId);
  };

  const toggleDuplicate = (prescriptionId, event) => {
    event.stopPropagation();
    setDuplicateFlags(prev => ({
      ...prev,
      [prescriptionId]: !prev[prescriptionId]
    }));
  };

  // Fetch fully populated prescription + (if needed) full appointment, then normalize payloads used by print/PDF
  const fetchAndPreparePrescription = async (prescription) => {
    // Fetch full prescription data with populated fields
    console.log('[Previous Rx] Fetching full prescription data for:', prescription._id);
    const response = await prescriptionAPI.getPrescriptionById(prescription._id);

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch prescription data');
    }

    const fullPrescription = response.data;
    console.log('[Previous Rx] Full prescription data:', fullPrescription);

    // Try to enrich appointment data if the populated one is sparse
    let appointmentData = fullPrescription.appointment;
    const hasClinicalDetails = appointmentData?.complaint ||
      appointmentData?.physicalExaminationFindings ||
      (appointmentData?.vitals && Object.keys(appointmentData.vitals || {}).length > 0);

    if (!hasClinicalDetails && appointmentData?._id) {
      console.log('[Previous Rx] Fetching full appointment data for:', appointmentData._id);
      try {
        const appointmentResponse = await appointmentAPI.getById(appointmentData._id);
        if (appointmentResponse.success && appointmentResponse.data) {
          appointmentData = appointmentResponse.data;
          console.log('[Previous Rx] Full appointment data:', appointmentData);
        }
      } catch (error) {
        console.error('[Previous Rx] Failed to fetch full appointment data:', error);
      }
    }

    // Patient snapshot with comprehensive fields
    const patientData = {
      firstName: fullPrescription.patient?.firstName || selectedPatient?.patient?.firstName || selectedPatient?.firstName || selectedPatient?.name || 'N/A',
      lastName: fullPrescription.patient?.lastName || selectedPatient?.patient?.lastName || selectedPatient?.lastName || '',
      sex: fullPrescription.patient?.sex || selectedPatient?.patient?.sex || selectedPatient?.sex || selectedPatient?.gender || 'N/A',
      gender: fullPrescription.patient?.sex || selectedPatient?.patient?.sex || selectedPatient?.sex || selectedPatient?.gender,
      mobile: fullPrescription.patient?.mobile || selectedPatient?.patient?.mobile || selectedPatient?.mobile || selectedPatient?.phone || 'N/A',
      email: fullPrescription.patient?.email || selectedPatient?.patient?.email || selectedPatient?.email,
      dob: fullPrescription.patient?.dob || selectedPatient?.patient?.dob || selectedPatient?.dob,
      dateOfBirth: fullPrescription.patient?.dateOfBirth || selectedPatient?.patient?.dateOfBirth || selectedPatient?.dateOfBirth,
      age: fullPrescription.patient?.age || selectedPatient?.patient?.age || selectedPatient?.age
    };

    // Doctor snapshot with all fields matching PatientDetails.jsx
    const doctorData = fullPrescription.doctor ? {
      firstName: typeof fullPrescription.doctor === 'object'
        ? fullPrescription.doctor.firstName
        : undefined,
      lastName: typeof fullPrescription.doctor === 'object'
        ? fullPrescription.doctor.lastName
        : undefined,
      name: typeof fullPrescription.doctor === 'object'
        ? (fullPrescription.doctor.name ||
           [fullPrescription.doctor.firstName, fullPrescription.doctor.lastName].filter(Boolean).join(' ').trim() ||
           'N/A')
        : fullPrescription.doctor,
      specialization: typeof fullPrescription.doctor === 'object'
        ? fullPrescription.doctor.specialization
        : undefined,
      qualification: typeof fullPrescription.doctor === 'object'
        ? fullPrescription.doctor.qualification
        : undefined,
      licenseNumber: typeof fullPrescription.doctor === 'object'
        ? fullPrescription.doctor.licenseNumber
        : undefined,
      registrationNumber: typeof fullPrescription.doctor === 'object'
        ? fullPrescription.doctor.registrationNumber
        : undefined
    } : null;

    // Merge vitals, complaint, and examination from prescription and appointment
    const mergedVitals = {
      ...(fullPrescription.vitals || {}),
      ...(appointmentData?.vitals || {}),
    };

    const mergedComplaint = (
      fullPrescription.chiefComplaint ||
      appointmentData?.chiefComplaint ||
      appointmentData?.complaint ||
      appointmentData?.presentingComplaint ||
      ''
    );

    const mergedExamination = (
      fullPrescription.physicalExaminationFindings ||
      appointmentData?.physicalExaminationFindings ||
      appointmentData?.examination ||
      appointmentData?.examFindings ||
      ''
    );

    // Core prescription payload with merged clinical data
    const prescriptionData = {
      language: fullPrescription.language || 'E',
      vitals: mergedVitals,
      chiefComplaint: mergedComplaint,
      physicalExaminationFindings: mergedExamination,
      items: fullPrescription.items || [],
      tests: fullPrescription.tests || [],
      imagingTests: fullPrescription.imagingTests || [],
      foodAdvice: fullPrescription.foodAdvice || '',
      knownCaseDisease: fullPrescription.knownCaseDisease,
      appointmentDate: fullPrescription.appointmentDate || fullPrescription.dateTime || appointmentData?.dateTime
    };

    // Optional slogan
    const selectedSlogan = fullPrescription.sloganRef ? {
      slogan: typeof fullPrescription.sloganRef === 'object'
        ? fullPrescription.sloganRef.slogan
        : fullPrescription.sloganRef
    } : null;

    return { fullPrescription, patientData, doctorData, prescriptionData, selectedSlogan, appointmentData };
  };

  const handleDownloadPDF = async (prescription, event) => {
    event.stopPropagation();
    setGeneratingPDF(prescription._id);
    
    try {
      const { fullPrescription, patientData, doctorData, prescriptionData, selectedSlogan } = await fetchAndPreparePrescription(prescription);

      console.log('[Previous Rx] Generating PDF with data:', { prescriptionData, patientData, doctorData });

      const isDuplicate = duplicateFlags[prescription._id] || false;

      const result = await generatePrescriptionPDF(
        prescriptionData,
        patientData,
        selectedSlogan,
        isDuplicate,
        {
          testsRecords: fullPrescription.tests || [],
          imagingTestsRecords: fullPrescription.imagingTests || [],
          doctorData
        }
      );

      if (!result.success) {
        showError('Error generating PDF: ' + result.error);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to generate PDF: ' + (error.message || 'Please try again.'));
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handlePrint = async (prescription, event) => {
    event.stopPropagation();
    
    try {
      const { fullPrescription, patientData, doctorData, prescriptionData, selectedSlogan } = await fetchAndPreparePrescription(prescription);

      console.log('[Previous Rx] Generating print HTML with data:', { prescriptionData, patientData, doctorData });

      const isDuplicate = duplicateFlags[prescription._id] || false;

      const printHTML = generatePrintHTML(
        prescriptionData,
        patientData,
        selectedSlogan,
        isDuplicate,
        {
          testsRecords: fullPrescription.tests || [],
          imagingTestsRecords: fullPrescription.imagingTests || [],
          doctorData
        }
      );

      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error('Error printing:', error);
      showError('Failed to print prescription: ' + (error.message || 'Please try again.'));
    }
  };

  if (!selectedPatient) return null;

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
        <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Previous Prescriptions</h3>
      </div>
      <div className="p-6">
        {loadingPrescriptions ? (
          <div className="text-center py-4">
            <div className="text-gray-500">Loading previous prescriptions...</div>
          </div>
        ) : sortedPrescriptions.length > 0 ? (
          <div className="space-y-2">
            {sortedPrescriptions.map((prescription, index) => (
              <div key={prescription._id} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                {/* Prescription Header - Always Visible */}
                <div 
                  className="flex justify-between items-center p-4 bg-gray-50 hover:bg-[#9DC9D4]/20 cursor-pointer transition-colors"
                  onClick={() => togglePrescription(prescription._id)}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#9DC9D4] flex items-center justify-center text-[#2D869C] font-bold text-md">
                        {sortedPrescriptions.length - index}
                      </div>
                      <div>
                        <h4 className="text-md font-semibold text-gray-600 uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>
                          Prescription #{sortedPrescriptions.length - index}
                        </h4>
                        <span className="text-md font-medium text-gray-500">
                          {new Date(prescription.createdAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Duplicate Checkbox */}
                    <label 
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={duplicateFlags[prescription._id] || false}
                        onChange={(e) => toggleDuplicate(prescription._id, e)}
                        className="w-3 h-3 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-md font-medium text-gray-700">Duplicate Print</span>
                    </label>
                    
                    {/* Download and Print Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleDownloadPDF(prescription, e)}
                        disabled={generatingPDF === prescription._id}
                        className="p-2 text-[#2D869C] hover:bg-[#9DC9D4]/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200"
                        title="Download PDF"
                      >
                        {generatingPDF === prescription._id ? (
                          <svg className="animate-spin w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={(e) => handlePrint(prescription, e)}
                        className="p-2 text-[#2D869C] hover:bg-[#9DC9D4]/20 rounded-lg transition-colors border border-gray-200"
                        title="Print"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                    </div>
                    
                    <svg 
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        expandedPrescription === prescription._id ? 'rotate-180' : ''
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Prescription Details - Expandable */}
                {expandedPrescription === prescription._id && (
                  <div className="p-5 bg-white border-t space-y-5">
                    {/* Metadata row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm bg-gradient-to-r from-gray-50 to-white p-4 rounded-xl border-2 border-gray-200">
                      <div className="flex flex-col">
                        <span className="text-lg font-semibold uppercase tracking-wide text-gray-600">Language</span>
                        <span className="text-md font-semibold text-gray-700 uppercase tracking-wide">{prescription.language === 'E' ? 'English' : prescription.language === 'H' ? 'Hindi' : prescription.language || 'N/A'}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-lg font-semibold uppercase tracking-wide text-gray-600">Created</span>
                        <span className="text-md font-semibold text-gray-700 uppercase tracking-wide">{new Date(prescription.createdAt).toLocaleString()}</span>
                      </div>
                      {prescription.knownCaseDisease && (
                        <div className="flex flex-col md:col-span-1">
                          <span className="text-lg font-semibold uppercase tracking-wide text-gray-600">Known Case</span>
                          <span className="text-md font-semibold text-gray-700 uppercase tracking-wide">
                            {typeof prescription.knownCaseDisease === 'object'
                              ? `${prescription.knownCaseDisease.name}${prescription.knownCaseDisease.code ? ` (${prescription.knownCaseDisease.code})` : ''}`
                              : prescription.knownCaseDisease}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Clinical context */}
                    {(prescription.chiefComplaint || prescription.physicalExaminationFindings || (prescription.vitals && Object.keys(prescription.vitals).length)) && (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {(prescription.chiefComplaint || prescription.physicalExaminationFindings) && (
                          <div className="lg:col-span-2 space-y-3">
                            {prescription.chiefComplaint && (
                              <div className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50/60">
                                <div className="text-lg font-semibold uppercase tracking-wide text-amber-700">Chief Complaint</div>
                                <div className="mt-1 text-md font-medium text-amber-900">{prescription.chiefComplaint}</div>
                              </div>
                            )}
                            {prescription.physicalExaminationFindings && (
                              <div className="p-4 rounded-xl border-2 border-indigo-200 bg-indigo-50/60">
                                <div className="text-lg font-semibold uppercase tracking-wide text-indigo-700">Physical Examination Findings</div>
                                <div className="mt-1 text-md font-medium text-indigo-900 whitespace-pre-wrap">{prescription.physicalExaminationFindings}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {prescription.vitals && Object.keys(prescription.vitals).length > 0 && (
                          <div className="p-4 rounded-xl border-2 border-teal-200 bg-teal-50/60">
                            <div className="text-lg font-semibold uppercase tracking-wide text-teal-700 mb-2">Vitals</div>
                            <div className="grid grid-cols-2 gap-2 text-lg font-medium text-teal-900">
                              {prescription.vitals.bpSys && prescription.vitals.bpDia && (
                                <div className="flex justify-between"><span>BP</span><span className="font-semibold">{prescription.vitals.bpSys}/{prescription.vitals.bpDia}</span></div>
                              )}
                              {prescription.vitals.pulse && (
                                <div className="flex justify-between"><span>Pulse</span><span className="font-semibold">{prescription.vitals.pulse} bpm</span></div>
                              )}
                              {prescription.vitals.temperature && (
                                <div className="flex justify-between"><span>Temp</span><span className="font-semibold">{prescription.vitals.temperature}°F</span></div>
                              )}
                              {prescription.vitals.oxygenSaturation && (
                                <div className="flex justify-between"><span>SpO2</span><span className="font-semibold">{prescription.vitals.oxygenSaturation}%</span></div>
                              )}
                              {prescription.vitals.weightKg && (
                                <div className="flex justify-between"><span>Weight</span><span className="font-semibold">{prescription.vitals.weightKg} kg</span></div>
                              )}
                              {prescription.vitals.heightCm && (
                                <div className="flex justify-between"><span>Height</span><span className="font-semibold">{prescription.vitals.heightCm} cm</span></div>
                              )}
                              {prescription.vitals.bmi && (
                                <div className="flex justify-between"><span>BMI</span><span className="font-semibold">{Number(prescription.vitals.bmi).toFixed(2)}</span></div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Medications table */}
                    {prescription.items && prescription.items.length > 0 && (
                      <div className="rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] border-b border-gray-200 flex items-center gap-2">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
                          <span className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Medications ({prescription.items.length})</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm text-left">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 w-10 text-lg font-semibold text-gray-600 uppercase tracking-wide">#</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Medicine</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Dose</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Frequency</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Route</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Days</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Instruction</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-md bg-white">
                              {prescription.items.map((item, itemIndex) => {
                                const med = typeof item?.medicine === 'object' ? item.medicine : {};
                                const name = item?.name || med.name || 'N/A';
                                const dose = item?.dose || med.dose || '';
                                const route = (item?.route || med.route || 'Oral')?.toString();
                                const timingLabel = formatTiming(item?.timing || med.timing || {});
                                const frequency = item?.frequency || (timingLabel || '');
                                const days = item?.days || item?.durationDays || med.days || '—';

                                return (
                                  <tr key={itemIndex} className="hover:bg-[#9DC9D4]/10">
                                    <td className="px-3 py-2 text-md font-semibold text-gray-500">{itemIndex + 1}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{name}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{dose || '—'}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{frequency || '—'}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{route ? route.charAt(0).toUpperCase() + route.slice(1) : '—'}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{days}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700">{item?.instruction || '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Tests table */}
                    {prescription.tests && prescription.tests.length > 0 && (
                      <div className="rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] border-b border-gray-200 flex items-center gap-2">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                          <span className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Tests ({prescription.tests.length})</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 w-10 text-lg font-semibold text-gray-600 uppercase tracking-wide">#</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Test</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {prescription.tests.map((test, idx) => {
                                const t = resolveTestObject(test);
                                return (
                                  <tr key={idx} className="hover:bg-[#9DC9D4]/10">
                                    <td className="px-3 py-2 text-md font-semibold text-gray-500">{idx + 1}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{t.name}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Imaging tests table */}
                    {prescription.imagingTests && prescription.imagingTests.length > 0 && (
                      <div className="rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] border-b border-gray-200 flex items-center gap-2">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <span className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Imaging ({prescription.imagingTests.length})</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm text-left">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 w-10 text-lg font-semibold text-gray-600 uppercase tracking-wide">#</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Modality</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Test</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Views</th>
                                <th className="px-3 py-2 text-lg font-semibold text-gray-600 uppercase tracking-wide">Instructions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {prescription.imagingTests.map((item, idx) => {
                                // Handle both old format (direct test object) and new format (nested with test, views, instructions)
                                const test = item.test || item;
                                const testName = typeof test === 'object' ? (test.testName || test.name) : 'Imaging Test';
                                const modality = item.modality || (typeof test === 'object' ? test.modality : null) || '—';
                                const selectedViews = item.selectedViews || [];
                                const instructions = item.instructions || '';
                                
                                return (
                                  <tr key={idx} className="hover:bg-[#9DC9D4]/10">
                                    <td className="px-3 py-2 text-lg font-semibold text-gray-500">{idx + 1}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{modality}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">{testName}</td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">
                                      {selectedViews.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                          {selectedViews.map((view, vIdx) => (
                                            <span key={vIdx} className="inline-block px-2 py-0.5 text-md font-semibold bg-[#9DC9D4]/30 text-[#2D869C] border border-[#9DC9D4] rounded-full uppercase tracking-wide">
                                              {view}
                                            </span>
                                          ))}
                                        </div>
                                      ) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-md font-semibold text-gray-700 uppercase tracking-wide">
                                      {instructions ? (
                                        <div className="max-w-xs whitespace-pre-wrap">{instructions}</div>
                                      ) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Advice / follow-up */}
                    {(prescription.foodAdvice || prescription.sloganRef) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {prescription.foodAdvice && (
                          <div className="p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/70">
                            <div className="text-lg font-semibold uppercase tracking-wide text-emerald-700 mb-1">Food Advice</div>
                            <p className="text-md font-semibold uppercase text-emerald-900 whitespace-pre-wrap">{prescription.foodAdvice}</p>
                          </div>
                        )}
                        {prescription.sloganRef && (
                          <div className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50/70">
                            <div className="text-lg font-semibold uppercase tracking-wide text-amber-700 mb-1">Slogan</div>
                            <p className="text-md font-semibold uppercase text-amber-900 ">
                              {typeof prescription.sloganRef === 'object' ? prescription.sloganRef.slogan : prescription.sloganRef}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No previous prescriptions found for this patient.
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviousPrescriptions;
